import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Wifi, Router, Server, Package, Zap, HardDrive, Pencil, Eye, Box, Network, Cable, Plug, Database, Cloud, Activity, Settings, EthernetPort } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { equipmentDatabase } from '../../data/equipment';
import { formatCurrency } from '../../utils/currencyUtils';
import SimpleLayoutEditor from '../Canvas/SimpleLayoutEditor';
import { resolveDeviceModel } from '../../engine/networkLogic';
import EquipmentPreviewPopover from './EquipmentPreviewPopover';
import { PortStage } from '../Canvas/PortStage';
import { summarizeEquipmentPreview } from '../../utils/equipmentPreview';
import ubiquitiLogo from '../../assets/ubiquiti.png';
import tplinkLogo from '../../assets/tplink.png';
import mikrotikLogo from '../../assets/mikrotik.png';

const MANUFACTURER_LOGOS: Record<string, { src: string; invert?: boolean }> = {
  ubiquiti: { src: ubiquitiLogo },
  ui: { src: ubiquitiLogo },
  'tp-link': { src: tplinkLogo },
  tplink: { src: tplinkLogo },
  mikrotik: { src: mikrotikLogo, invert: true },
};

const MANUFACTURER_OUTLINE: Record<string, string> = {
  ubiquiti: '#3b82f6',
  ui: '#3b82f6',
  'tp-link': '#22d3ee',
  tplink: '#22d3ee',
  mikrotik: '#ef4444',
};

function manufacturerOutline(manufacturer?: string, isDark = true): string {
  return MANUFACTURER_OUTLINE[String(manufacturer || '').toLowerCase()] || (isDark ? '#475569' : '#d1d5db');
}

function ManufacturerLogo({
  manufacturer,
  variant = 'corner',
  isDark = true,
}: {
  manufacturer?: string;
  variant?: 'corner' | 'inline';
  isDark?: boolean;
}) {
  const logo = MANUFACTURER_LOGOS[String(manufacturer || '').toLowerCase()];
  const boxClass = variant === 'inline'
    ? 'w-12 h-6 flex-shrink-0 justify-start'
    : 'absolute top-2 right-2 w-14 h-8 pointer-events-none justify-center';
  return (
    <div className={`${boxClass} flex items-center`}>
      {logo ? (
        <img
          src={logo.src}
          alt=""
          className={`object-contain ${
            variant === 'inline' ? 'w-full h-full object-left' : 'max-w-full max-h-full'
          } ${logo.invert && isDark ? 'invert' : ''}`}
        />
      ) : (
        <span className={`text-[10px] capitalize truncate max-w-full ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {manufacturer}
        </span>
      )}
    </div>
  );
}

const EquipmentModal: React.FC = () => {
  const { isAddEquipmentOpen, setAddEquipmentOpen, setDraggedItem, currency, saveEquipmentConfig, getEquipmentConfig, appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const equipmentCustomizations = useProjectStore((s) => s.equipmentCustomizations);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [editingEquipment, setEditingEquipment] = useState<any | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [equipmentView] = useState<'tiles' | 'list'>('list');
  const [expandedEquipmentIds, setExpandedEquipmentIds] = useState<string[]>([]);
  const [previewEquipment, setPreviewEquipment] = useState<any | null>(null);
  const [previewAnchor, setPreviewAnchor] = useState<HTMLElement | null>(null);
  const previewCloseTimer = useRef<number | null>(null);

  const openPreview = (equipment: any, anchor: HTMLElement) => {
    if (previewCloseTimer.current) {
      window.clearTimeout(previewCloseTimer.current);
      previewCloseTimer.current = null;
    }
    setPreviewEquipment(equipment);
    setPreviewAnchor(anchor);
  };

  const scheduleClosePreview = () => {
    if (previewCloseTimer.current) window.clearTimeout(previewCloseTimer.current);
    previewCloseTimer.current = window.setTimeout(() => {
      setPreviewEquipment(null);
      setPreviewAnchor(null);
      previewCloseTimer.current = null;
    }, 160);
  };

  const clearOpenedEquipmentInfo = () => {
    setExpandedEquipmentIds([]);
    setPreviewEquipment(null);
    setPreviewAnchor(null);
    if (previewCloseTimer.current) {
      window.clearTimeout(previewCloseTimer.current);
      previewCloseTimer.current = null;
    }
  };

  useEffect(() => {
    if (equipmentView !== 'list') setExpandedEquipmentIds([]);
  }, [equipmentView]);

  useEffect(() => {
    if (!isAddEquipmentOpen) clearOpenedEquipmentInfo();
  }, [isAddEquipmentOpen]);

  const toggleExpandedEquipment = (equipmentId: string) => {
    setExpandedEquipmentIds((prev) =>
      prev.includes(equipmentId) ? prev.filter((id) => id !== equipmentId) : [...prev, equipmentId]
    );
  };

  // Get all unique categories/types from equipment database (including custom ones)
  // Recalculate whenever the modal opens or equipment database changes
  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    equipmentDatabase.forEach((eq: any) => {
      if (eq.type) {
        categories.add(eq.type);
      }
    });
    // Sort categories, but keep 'all' first
    const sortedCategories = Array.from(categories).sort();
    return ['all', ...sortedCategories];
  }, [isAddEquipmentOpen, equipmentDatabase.length]); // Recalculate when modal opens or equipment count changes

  if (!isAddEquipmentOpen) return null;

  const filteredEquipment = equipmentDatabase.filter(equipment => {
    const matchesSearch = equipment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         equipment.manufacturer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || equipment.type === selectedType;
    return matchesSearch && matchesType;
  });

  const handleEquipmentClick = (equipment: any) => {
    clearOpenedEquipmentInfo();
    setDraggedItem({ type: 'equipment', equipment });
    setAddEquipmentOpen(false);
  };

  const handleEditLayout = (e: React.MouseEvent, equipment: any) => {
    e.stopPropagation(); // Prevent triggering the card click
    clearOpenedEquipmentInfo();
    setEditingEquipment(equipment);
  };

  const handleSaveLayout = (layout: any, equipmentData?: any) => {
    const deviceModel = equipmentData?.deviceModel;
    if (isCreatingNew && equipmentData) {
      const newEquipment: any = {
        id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: equipmentData.name,
        type: equipmentData.type,
        manufacturer: equipmentData.manufacturer,
        price: equipmentData.price,
        specifications: equipmentData.specifications || {},
      };
      
      equipmentDatabase.push(newEquipment);
      saveEquipmentConfig(newEquipment.id, { layout, ...(deviceModel ? { deviceModel } : {}) });
      useProjectStore.getState().saveProject();
      
      setIsCreatingNew(false);
      setAddEquipmentOpen(false);
    } else if (editingEquipment) {
      const existing = getEquipmentConfig(editingEquipment.id) || {};
      saveEquipmentConfig(editingEquipment.id, { ...existing, layout, ...(deviceModel ? { deviceModel } : {}) });
      useProjectStore.getState().saveProject();
      setEditingEquipment(null);
    }
  };

  const getIcon = (equipment: any) => {
    // If equipment has a custom icon field, use it
    if (equipment.icon) {
      const iconMap: { [key: string]: any } = {
        Package, Router, Switch: EthernetPort, Wifi, Server, Zap, HardDrive, Box, Network, Cable, Plug, Database, Cloud, Activity, Settings
      };
      const IconComponent = iconMap[equipment.icon] || Package;
      return <IconComponent size={16} className="text-blue-500" />;
    }
    
    // Otherwise, fall back to type-based icons
    switch (equipment.type) {
      case 'ap':
        return <Wifi size={16} className="text-yellow-500" />;
      case 'switch':
        return <Router size={16} className="text-blue-500" />;
      case 'router':
        return <Router size={16} className="text-red-500" />;
      case 'server':
        return <Server size={16} className="text-purple-500" />;
      case 'patch-panel':
        return <Package size={16} className="text-orange-500" />;
      case 'poe-injector':
        return <Zap size={16} className="text-pink-500" />;
      case 'psu':
        return <Zap size={16} className="text-lime-500" />;
      case 'ups':
        return <HardDrive size={16} className="text-indigo-500" />;
      default:
        return <Package size={16} className="text-gray-500" />;
    }
  };

  const selectBg = isDark ? '#1e293b' : '#ffffff';
  const selectFg = isDark ? '#ffffff' : '#111827';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90]">
      <div className={`backdrop-blur-sm rounded-lg shadow-xl border w-[90vw] max-w-7xl h-[85vh] flex flex-col overflow-hidden ${
        isDark ? 'bg-slate-800/90 border-slate-700/50' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
          <h2 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add Equipment</h2>
          <button
            onClick={() => setAddEquipmentOpen(false)}
            className={`p-2 rounded-md transition-colors ${
              isDark ? 'hover:bg-slate-700/50 text-gray-300 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Header with Search and Create New */}
        <div className={`p-6 border-b flex items-center justify-between gap-4 ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
          <div className="relative flex-1">
            <Search size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                isDark
                  ? 'bg-slate-700/50 border-slate-600 text-white placeholder-gray-400'
                  : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
          <button
            onClick={() => {
              setIsCreatingNew(true);
            }}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
              isDark
                ? 'bg-gradient-to-br from-blue-900/50 to-purple-900/50 border border-blue-500/30 text-white hover:from-blue-800/60 hover:to-purple-800/60 hover:border-blue-400/40'
                : 'bg-blue-50 border border-blue-200 text-blue-800 hover:bg-blue-100'
            }`}
          >
            + Create New
          </button>
        </div>

        {/* Main Content Area - Sidebar + Equipment Grid */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Category Filter */}
          <div className={`w-64 border-r flex flex-col ${isDark ? 'border-slate-700/50 bg-slate-800/30' : 'border-gray-200 bg-gray-50'}`}>
            <div className={`p-4 border-b ${isDark ? 'border-slate-700/50' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Categories</h3>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isDark ? 'border-slate-600' : 'border-gray-300'
                }`}
                style={{
                  backgroundColor: selectBg,
                  color: selectFg
                }}
              >
                {availableCategories.map(type => (
                  <option 
                    key={type} 
                    value={type}
                    style={{ backgroundColor: selectBg, color: selectFg }}
                  >
                    {type === 'all' ? 'All Categories' : type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className={`text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <p className={`font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Quick Filters</p>
                {availableCategories.filter(cat => cat !== 'all').map(type => (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                      selectedType === type
                        ? (isDark ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30' : 'bg-blue-50 text-blue-700 border border-blue-200')
                        : (isDark ? 'text-gray-400 hover:text-white hover:bg-slate-700/30' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100')
                    }`}
                  >
                    {type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Equipment List */}
          <div className={`flex-1 overflow-y-auto p-6 ${isDark ? '' : 'bg-white'}`}>
            {equipmentView === 'list' ? (
              <div className={`flex flex-col divide-y ${isDark ? 'divide-slate-700/70' : 'divide-gray-200'}`}>
                {filteredEquipment.map((equipment) => {
                  const isExpanded = expandedEquipmentIds.includes(equipment.id);
                  const specs = equipment.specifications || {};
                  return (
                    <div key={equipment.id} className="group">
                      <div
                        onClick={() => toggleExpandedEquipment(equipment.id)}
                        className={`relative flex items-center gap-3 px-2 py-1.5 cursor-pointer ${
                          isExpanded
                            ? (isDark ? 'bg-slate-700/70' : 'bg-gray-100')
                            : (isDark ? 'hover:bg-slate-700/60' : 'hover:bg-gray-100')
                        }`}
                      >
                        <ManufacturerLogo manufacturer={equipment.manufacturer} variant="inline" isDark={isDark} />
                        <span className={`text-sm truncate min-w-0 flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{equipment.name}</span>
                        <span className={`text-xs capitalize flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{equipment.type}</span>
                        <button
                          onClick={(e) => handleEditLayout(e, equipment)}
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 text-gray-300 hover:bg-blue-600 hover:text-white"
                          title="Edit Layout"
                        >
                          <Pencil size={13} />
                        </button>
                      </div>
                      {isExpanded && (() => {
                        const savedConfig = equipmentCustomizations[equipment.id]?.config || getEquipmentConfig(equipment.id);
                        const summary = summarizeEquipmentPreview(equipment, savedConfig);
                        const layout = summary.layout;
                        const previewWidth = 420;
                        const scale = layout ? Math.min(1, previewWidth / Math.max(layout.stage.width, 1)) : 1;
                        return (
                        <div className={`px-2 pb-2 pt-1 ${isDark ? 'bg-slate-700/40' : 'bg-gray-50'}`}>
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-44">
                              <div className={`text-xs space-y-0.5 mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                <div className="capitalize">Manufacturer: {equipment.manufacturer}</div>
                                {specs.ports != null && <div>Ports: {specs.ports}</div>}
                                {specs.power && <div>Power: {specs.power}</div>}
                                {specs.speed && <div>Speed: {specs.speed}</div>}
                                {specs.coverage && <div>Coverage: {specs.coverage}</div>}
                                {specs.features && <div>Features: {specs.features}</div>}
                                {summary.features.map((feature) => (
                                  <div key={feature.key} className="font-medium" style={{ color: feature.color }}>
                                    {feature.label}
                                  </div>
                                ))}
                                {equipment.price != null && (
                                  <div className={`font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                    {formatCurrency(equipment.price, currency)}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEquipmentClick(equipment);
                                }}
                                className="px-3 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
                              >
                                Add
                              </button>
                            </div>
                            <div className="flex-1 min-w-0 flex justify-end">
                            {layout ? (
                              <div
                                className="overflow-hidden rounded-md"
                                style={{
                                  width: layout.stage.width * scale,
                                  height: layout.stage.height * scale,
                                }}
                              >
                                <div
                                  style={{
                                    width: layout.stage.width,
                                    height: layout.stage.height,
                                    transform: `scale(${scale})`,
                                    transformOrigin: 'top left',
                                  }}
                                >
                                  <PortStage layout={layout} hideNumbers colourizePorts />
                                </div>
                              </div>
                            ) : (
                              <div className={`h-16 w-full max-w-md flex items-center justify-center text-[11px] rounded-md border border-dashed ${
                                isDark ? 'text-gray-500 border-slate-600' : 'text-gray-400 border-gray-300'
                              }`}>
                                No port layout
                              </div>
                            )}
                            </div>
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredEquipment.map(equipment => (
              <div
                key={equipment.id}
                onClick={() => handleEquipmentClick(equipment)}
                className={`group relative border rounded-lg p-4 cursor-pointer transition-colors ${
                  isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={{ borderColor: manufacturerOutline(equipment.manufacturer, isDark) }}
              >
                <ManufacturerLogo manufacturer={equipment.manufacturer} isDark={isDark} />
                <div className="flex items-start space-x-3 pr-16">
                  {getIcon(equipment)}
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{equipment.name}</h3>
                    <p className={`text-xs capitalize ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{equipment.type}</p>
                    
                    {equipment.specifications && (
                      <div className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {equipment.specifications.ports && (
                          <div>Ports: {equipment.specifications.ports}</div>
                        )}
                        {equipment.specifications.power && (
                          <div>Power: {equipment.specifications.power}</div>
                        )}
                        {equipment.specifications.speed && (
                          <div>Speed: {equipment.specifications.speed}</div>
                        )}
                      </div>
                    )}
                    
                    {equipment.price && (
                      <div className="mt-2 text-sm font-medium text-green-400">
                        {formatCurrency(equipment.price, currency)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="absolute top-11 right-2 flex flex-col gap-1 z-10">
                  <button
                    onClick={(e) => handleEditLayout(e, equipment)}
                    className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    title="Edit Layout"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (previewEquipment?.id === equipment.id) {
                        setPreviewEquipment(null);
                        setPreviewAnchor(null);
                        return;
                      }
                      openPreview(equipment, e.currentTarget);
                    }}
                    onMouseEnter={(e) => openPreview(equipment, e.currentTarget)}
                    onMouseLeave={scheduleClosePreview}
                    className={`w-6 h-6 rounded-full text-white transition-opacity flex items-center justify-center ${
                      previewEquipment?.id === equipment.id
                        ? 'opacity-100 bg-slate-500 hover:bg-slate-400'
                        : 'opacity-0 group-hover:opacity-100 bg-slate-600 hover:bg-slate-500'
                    }`}
                    title="Preview ports"
                  >
                    <Eye size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
            )}

          {filteredEquipment.length === 0 && (
            <div className="text-center py-12">
              <Package size={48} className={`mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No equipment found</h3>
              <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Try adjusting your search or filter criteria</p>
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className={`p-6 border-t ${isDark ? 'border-slate-700/50 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Click on any equipment to add it to your canvas. You can drag and position it after adding.
          </p>
        </div>
      </div>

      {/* Layout Editor Modal */}
      {(editingEquipment || isCreatingNew) && createPortal(
        <SimpleLayoutEditor
          onSave={handleSaveLayout}
          onClose={() => {
            setEditingEquipment(null);
            setIsCreatingNew(false);
          }}
          equipmentName={editingEquipment?.name}
          equipmentManufacturer={editingEquipment?.manufacturer}
          equipmentLabel={undefined} // Don't show label when editing from modal
          initialLayout={editingEquipment ? (getEquipmentConfig(editingEquipment.id)?.layout || undefined) : undefined}
          portCount={editingEquipment?.specifications?.ports || 0}
          isCreatingNew={isCreatingNew}
          enableElectrical
          initialDeviceModel={editingEquipment ? resolveDeviceModel(editingEquipment.id, editingEquipment.type, getEquipmentConfig(editingEquipment.id)).model : undefined}
          equipmentType={editingEquipment?.type}
          equipmentId={editingEquipment?.id}
        />,
        document.body
      )}

      {previewEquipment && (
        <EquipmentPreviewPopover
          isDark={isDark}
          equipment={previewEquipment}
          savedConfig={equipmentCustomizations[previewEquipment.id]?.config || getEquipmentConfig(previewEquipment.id)}
          anchorEl={previewAnchor}
          onMouseEnter={() => {
            if (previewCloseTimer.current) {
              window.clearTimeout(previewCloseTimer.current);
              previewCloseTimer.current = null;
            }
          }}
          onMouseLeave={scheduleClosePreview}
          onClose={() => {
            setPreviewEquipment(null);
            setPreviewAnchor(null);
          }}
        />
      )}
    </div>
  );
};

export default EquipmentModal;
